/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator } from "@azure/core-paging";
import { SimplePollerLike, OperationState } from "@azure/core-lro";
import {
  NetworkSecurityPerimeterConfiguration,
  NetworkSecurityPerimeterConfigurationsListByServiceOptionalParams,
  NetworkSecurityPerimeterConfigurationsGetOptionalParams,
  NetworkSecurityPerimeterConfigurationsGetResponse,
  NetworkSecurityPerimeterConfigurationsReconcileOptionalParams,
  NetworkSecurityPerimeterConfigurationsReconcileResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a NetworkSecurityPerimeterConfigurations. */
export interface NetworkSecurityPerimeterConfigurations {
  /**
   * Gets a list of network security perimeter configurations for a search service.
   * @param resourceGroupName The name of the resource group within the current subscription. You can
   *                          obtain this value from the Azure Resource Manager API or the portal.
   * @param searchServiceName The name of the Azure AI Search service associated with the specified
   *                          resource group.
   * @param options The options parameters.
   */
  listByService(
    resourceGroupName: string,
    searchServiceName: string,
    options?: NetworkSecurityPerimeterConfigurationsListByServiceOptionalParams,
  ): PagedAsyncIterableIterator<NetworkSecurityPerimeterConfiguration>;
  /**
   * Gets a network security perimeter configuration.
   * @param resourceGroupName The name of the resource group within the current subscription. You can
   *                          obtain this value from the Azure Resource Manager API or the portal.
   * @param searchServiceName The name of the Azure AI Search service associated with the specified
   *                          resource group.
   * @param nspConfigName The network security perimeter configuration name.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    searchServiceName: string,
    nspConfigName: string,
    options?: NetworkSecurityPerimeterConfigurationsGetOptionalParams,
  ): Promise<NetworkSecurityPerimeterConfigurationsGetResponse>;
  /**
   * Reconcile network security perimeter configuration for the Azure AI Search resource provider. This
   * triggers a manual resync with network security perimeter configurations by ensuring the search
   * service carries the latest configuration.
   * @param resourceGroupName The name of the resource group within the current subscription. You can
   *                          obtain this value from the Azure Resource Manager API or the portal.
   * @param searchServiceName The name of the Azure AI Search service associated with the specified
   *                          resource group.
   * @param nspConfigName The network security perimeter configuration name.
   * @param options The options parameters.
   */
  beginReconcile(
    resourceGroupName: string,
    searchServiceName: string,
    nspConfigName: string,
    options?: NetworkSecurityPerimeterConfigurationsReconcileOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<NetworkSecurityPerimeterConfigurationsReconcileResponse>,
      NetworkSecurityPerimeterConfigurationsReconcileResponse
    >
  >;
  /**
   * Reconcile network security perimeter configuration for the Azure AI Search resource provider. This
   * triggers a manual resync with network security perimeter configurations by ensuring the search
   * service carries the latest configuration.
   * @param resourceGroupName The name of the resource group within the current subscription. You can
   *                          obtain this value from the Azure Resource Manager API or the portal.
   * @param searchServiceName The name of the Azure AI Search service associated with the specified
   *                          resource group.
   * @param nspConfigName The network security perimeter configuration name.
   * @param options The options parameters.
   */
  beginReconcileAndWait(
    resourceGroupName: string,
    searchServiceName: string,
    nspConfigName: string,
    options?: NetworkSecurityPerimeterConfigurationsReconcileOptionalParams,
  ): Promise<NetworkSecurityPerimeterConfigurationsReconcileResponse>;
}
